import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Search, Trash2, UserCheck, UserX, ChevronLeft, ChevronRight, Loader2, Shield, Store, Globe, Mail, CheckSquare, Square, Send } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import PurgeEmailButton from './PurgeEmailButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.4)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const roleBadge = (roles) => {
  if (roles?.includes('vendor')) return { label: 'Vendor', color: 'bg-purple-500/15 text-purple-400 border-purple-500/20', icon: Store };
  if (roles?.includes('supporter')) return { label: 'Supporter', color: 'bg-green-500/15 text-green-400 border-green-500/20', icon: Shield };
  return { label: 'Consumer', color: 'bg-blue-500/15 text-blue-400 border-blue-500/20', icon: Users };
};

const MESSAGE_TEMPLATES = [
  {
    id: 'welcome_customer',
    name: 'Welcome — Customer',
    forRole: 'consumer',
    content: `Welcome to EntrepreNatives! 🌿

We are so glad you're choosing to support Indigenous businesses. By being here, you're helping amplify Indigenous excellence and making a real difference in our communities.

Browse our talented vendors, discover unique services, and know that every purchase directly supports Indigenous entrepreneurs.

If you have any questions at all, feel free to message us right back here — we're real people and we'd love to hear from you.

With gratitude,
The EntrepreNatives Creators`
  },
  {
    id: 'welcome_vendor',
    name: 'Welcome — Vendor',
    forRole: 'vendor',
    content: `Welcome to EntrepreNatives! 🌿

We are honored to have you join our platform dedicated to amplifying Indigenous businesses and celebrating Indigenous excellence. Your talent and craft deserve to be seen by the world, and we're here to help make that happen.

Set up your profile, list your services, and start connecting with customers who are eager to support Indigenous entrepreneurs just like you.

If you have any questions or need help getting started, message us right back here — we're real people and we're rooting for your success.

In solidarity,
The EntrepreNatives Creators`
  }
];

export default function MasterUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showMessageComposer, setShowMessageComposer] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const [sending, setSending] = useState(false);
  const limit = 20;

  const token = localStorage.getItem('masterToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchUsers(); }, [page, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.set('search', search);
      if (roleFilter) params.set('role', roleFilter);
      const res = await fetch(`${API}/master/users/?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setTotal(data.total);
      }
    } catch {} finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchUsers(); };

  const toggleSelect = (userId) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.user_id)));
    }
  };

  const toggleStatus = async (userId, currentActive) => {
    try {
      const res = await fetch(`${API}/master/users/${userId}/status`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentActive })
      });
      if (res.ok) { toast.success(currentActive ? 'User deactivated' : 'User activated'); fetchUsers(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/master/users/${deleteTarget.user_id}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('User removed'); setDeleteTarget(null); fetchUsers(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); }
  };

  const applyTemplate = (template) => {
    setMessageContent(template.content);
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim()) { toast.error('Please write a message'); return; }
    if (!sendToAll && selectedUsers.size === 0) { toast.error('Please select recipients or choose "Send to All"'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/master/users/mass-message`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_ids: sendToAll ? [] : Array.from(selectedUsers),
          send_to_all: sendToAll,
          content: messageContent
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Message sent to ${data.count} users!`);
        setShowMessageComposer(false);
        setMessageContent('');
        setSelectedUsers(new Set());
        setSendToAll(false);
      } else { const d = await res.json(); toast.error(d.detail || 'Failed to send'); }
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  const totalPages = Math.ceil(total / limit);
  const allSelected = users.length > 0 && selectedUsers.size === users.length;

  return (
    <div className="space-y-6" data-testid="master-users-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white font-syne flex items-center gap-2">
            <Users size={24} className="text-[#A78BFA]" /> Marketplace Users
          </h1>
          <p className="text-sm text-[#9CA3AF] mt-1">{total} total users{selectedUsers.size > 0 && ` — ${selectedUsers.size} selected`}</p>
        </div>
        <div className="flex items-center gap-2">
          <PurgeEmailButton />
          <Button
            onClick={() => setShowMessageComposer(true)}
            className="bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-sm rounded-xl gap-2"
            data-testid="open-mass-message"
          >
            <Mail size={16} /> Message Users
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <GlassCard className="p-5">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..." className="bg-white/5 border-white/10 text-white pl-10 rounded-xl h-10" data-testid="user-search-input" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['', 'consumer', 'vendor', 'supporter'].map(r => (
              <button key={r} type="button" onClick={() => { setRoleFilter(r); setPage(1); }}
                className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${roleFilter === r ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border border-[#8B5CF6]/30' : 'text-[#9CA3AF] hover:text-white border border-white/10'}`}
                data-testid={`filter-${r || 'all'}`}
              >
                {r ? r.charAt(0).toUpperCase() + r.slice(1) : 'All'}
              </button>
            ))}
          </div>
          <Button type="submit" className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm rounded-xl" data-testid="user-search-btn">Search</Button>
        </form>
      </GlassCard>

      {/* Users Table */}
      <GlassCard className="overflow-hidden">
        {/* Select All Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-white/[0.02]">
          <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-white transition-colors" data-testid="select-all-users">
            {allSelected ? <CheckSquare size={18} className="text-[#A78BFA]" /> : <Square size={18} />}
            <span>{allSelected ? 'Deselect All' : 'Select All'}</span>
          </button>
          {selectedUsers.size > 0 && (
            <button onClick={() => { setShowMessageComposer(true); }} className="text-xs text-[#A78BFA] hover:text-white transition-colors flex items-center gap-1">
              <Mail size={14} /> Message {selectedUsers.size} selected
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-[#8B5CF6]" /></div>
        ) : users.length === 0 ? (
          <div className="text-center py-12"><Users size={32} className="mx-auto mb-3 text-[#64748B]" /><p className="text-[#9CA3AF]">No users found</p></div>
        ) : (
          <div className="divide-y divide-white/5">
            {users.map(user => {
              const badge = roleBadge(user.roles);
              const isActive = user.is_active !== false;
              const isAdmin = user.email === 'admin@entreprenatives.com' || user.email === 'master@entreprenatives.com';
              const isSelected = selectedUsers.has(user.user_id);
              return (
                <div key={user.user_id} className={`flex items-center gap-3 p-4 hover:bg-white/5 transition-colors ${!isActive ? 'opacity-50' : ''}`} data-testid={`user-row-${user.user_id}`}>
                  {/* Checkbox */}
                  <button onClick={() => toggleSelect(user.user_id)} className="flex-shrink-0" data-testid={`check-${user.user_id}`}>
                    {isSelected ? <CheckSquare size={18} className="text-[#A78BFA]" /> : <Square size={18} className="text-[#64748B]" />}
                  </button>
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8B5CF6]/40 to-[#3B82F6]/40 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                    {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                  </div>
                  {/* Info — name clickable to detail */}
                  <Link to={`/master/users/${user.user_id}`} className="min-w-0 flex-1 group" data-testid={`user-link-${user.user_id}`}>
                    <p className="text-white text-sm font-medium truncate group-hover:text-[#A78BFA] transition-colors">{user.name || 'No name'}</p>
                    <p className="text-[#64748B] text-xs truncate">{user.email}</p>
                    {user.vendor_info?.business_name && (
                      <p className="text-[#C4B5FD] text-[11px] truncate" data-testid={`user-biz-${user.user_id}`}>
                        {user.vendor_info.business_name}
                        {user.vendor_info.owner_name ? ` · ${user.vendor_info.owner_name}` : ''}
                      </p>
                    )}
                    {(user.vendor_info?.age || user.vendor_info?.gender) && (
                      <p className="text-[#9CA3AF] text-[10px] truncate" data-testid={`user-demo-${user.user_id}`}>
                        {user.vendor_info?.age ? `${user.vendor_info.age} yrs` : ''}
                        {user.vendor_info?.age && user.vendor_info?.gender ? ' · ' : ''}
                        {user.vendor_info?.gender ? user.vendor_info.gender.replace('_', '-') : ''}
                      </p>
                    )}
                    {user.tribal_affiliation && <p className="text-[#9CA3AF] text-[10px] truncate">{user.tribal_affiliation}</p>}
                  </Link>
                  {/* Badges & Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`hidden sm:flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border ${badge.color}`}>
                      <badge.icon size={10} /> {badge.label}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isActive ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className="hidden md:block text-[10px] text-[#64748B]">{user.created_at ? new Date(user.created_at).toLocaleDateString() : ''}</span>
                    {!isAdmin && (
                      <>
                        <button onClick={() => toggleStatus(user.user_id, isActive)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title={isActive ? 'Deactivate' : 'Activate'} data-testid={`toggle-${user.user_id}`}>
                          {isActive ? <UserX size={14} className="text-yellow-400" /> : <UserCheck size={14} className="text-green-400" />}
                        </button>
                        <button onClick={() => setDeleteTarget(user)} className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors" title="Delete" data-testid={`delete-${user.user_id}`}>
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      </>
                    )}
                    {isAdmin && <Shield size={14} className="text-[#A78BFA]" title="Protected" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </GlassCard>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-xl border border-white/10 text-[#9CA3AF] disabled:opacity-30 hover:bg-white/5"><ChevronLeft size={16} /></button>
          <span className="text-[#9CA3AF] text-sm">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-xl border border-white/10 text-[#9CA3AF] disabled:opacity-30 hover:bg-white/5"><ChevronRight size={16} /></button>
        </div>
      )}

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-lg">Remove User</DialogTitle></DialogHeader>
          <p className="text-[#9CA3AF] text-sm">Permanently remove <span className="text-white font-bold">{deleteTarget?.name || deleteTarget?.email}</span>?</p>
          <div className="flex gap-3 mt-4">
            <Button onClick={() => setDeleteTarget(null)} variant="outline" className="flex-1 border-white/10 text-[#9CA3AF]">Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} className="flex-1 bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-user">
              {deleting ? 'Removing...' : 'Remove'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mass Message Composer */}
      <Dialog open={showMessageComposer} onOpenChange={setShowMessageComposer}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-syne flex items-center gap-2"><Mail size={20} className="text-[#A78BFA]" /> Message Users</DialogTitle>
          </DialogHeader>

          {/* Recipients */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#9CA3AF]">Recipients</p>
              <button
                onClick={() => setSendToAll(!sendToAll)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${sendToAll ? 'bg-[#8B5CF6]/20 text-[#A78BFA] border border-[#8B5CF6]/30' : 'text-[#9CA3AF] border border-white/10 hover:text-white'}`}
                data-testid="send-to-all-toggle"
              >
                {sendToAll ? <CheckSquare size={14} /> : <Square size={14} />} Send to ALL Users
              </button>
            </div>
            {!sendToAll && (
              <p className="text-xs text-[#64748B]">
                {selectedUsers.size > 0 ? `${selectedUsers.size} user${selectedUsers.size > 1 ? 's' : ''} selected from the list` : 'Select users from the list first, or click "Send to ALL"'}
              </p>
            )}
            {sendToAll && <p className="text-xs text-green-400">Message will be sent to all {total} users on the platform</p>}
          </div>

          {/* Templates */}
          <div className="space-y-2">
            <p className="text-sm text-[#9CA3AF]">Quick Templates</p>
            <div className="flex gap-2 flex-wrap">
              {MESSAGE_TEMPLATES.map(t => (
                <button
                  key={t.id}
                  onClick={() => applyTemplate(t)}
                  className="px-3 py-2 rounded-xl text-xs font-medium text-[#9CA3AF] border border-white/10 hover:text-white hover:border-[#8B5CF6]/30 hover:bg-[#8B5CF6]/10 transition-all"
                  data-testid={`template-${t.id}`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          {/* Message Content */}
          <div>
            <Textarea
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="Type your message here..."
              className="bg-white/5 border-white/10 text-white rounded-xl min-h-[200px] placeholder:text-white/20"
              data-testid="mass-message-content"
            />
            <p className="text-[10px] text-[#64748B] mt-1">{messageContent.length} characters</p>
          </div>

          {/* Send */}
          <Button
            onClick={handleSendMessage}
            disabled={sending || (!sendToAll && selectedUsers.size === 0) || !messageContent.trim()}
            className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white rounded-xl gap-2 disabled:opacity-40"
            data-testid="send-mass-message"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? 'Sending...' : sendToAll ? `Send to All ${total} Users` : `Send to ${selectedUsers.size} User${selectedUsers.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
